import { hasJoinedMuse2AlphaPhase2 } from "../../../src/shared/muse2AlphaEnrollment.js";
import { getConferenceCampaign, requiresConferencePayment } from "../../../src/shared/conferenceCampaign";
import { OVERAGE_COST, PRICING_CREDIT_PACKS, PRICING_PACKS, PRICING_PLANS } from "../../../src/shared/constants.js";
import { manuscriptFeedbackPaidPlanTitleAllowed } from "../../../src/shared/manuscriptFeedbackPlanAccess.js";
import { isQATurnstileBypassEmail } from "../../../src/shared/qaTurnstileBypass.js";
import { hasTaxLocationAccessBlock } from "../../../src/shared/taxLocationAccess.js";
import { effectiveAdminForFeatures as computeEffectiveAdminForFeatures } from "../shared/subscriptionAdminPreview";
import Auth from "../Auth.js";
import { hasAdminPermission } from "../stores/adminAccessStore";
import firebase from "../Firebase.js";
import Utils, { hash } from "../Utils.js";

const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const db = firebase.firestore();
const collection = db.collection("subscriptions");

const statsCacheByUserId = new Map();
const statsFetchByUserId = new Map();

/**
 * Feature rollout percentages (0-100).
 * Increasing a value will only add users, never remove existing ones.
 */
export const FEATURE_ROLLOUT = {};

export default class Subscription {
  constructor(userId) {
    if (!userId) {
      throw "Must provide userId";
    }
    this.userId = userId;
    this.data = {};
    this.fetched = false;
  }

  // Fetches and listens to changes from the subscription object
  async fetchAndListen(callback) {
    return new Promise((resolve, reject) => {
      let initialHandled = false;
      const unsubscribe = collection.doc(this.userId).onSnapshot(
        (doc) => {
          if (!initialHandled) {
            initialHandled = true;
            if (!doc.exists) {
              unsubscribe();
              resolve(false);
              return;
            }

            this.updateData(doc.data());
            this.fetched = true;
            if (callback) callback(this);
            this.fetchStats()
              .then(() => {
                if (callback) callback(this);
                resolve(this);
              })
              .catch(reject);
            return;
          }

          if (doc.data()) {
            this.updateData(doc.data());
          }
          if (callback) callback(this);
        },
        (error) => {
          if (!initialHandled) {
            initialHandled = true;
            unsubscribe();
            reject(error);
          }
        },
      );
    });
  }

  // Fetches additional stats for the subscription
  async fetchStats() {
    if (!this.fetched) {
      throw new Error("Must be fetched before getting stats");
    }

    // Stats are already fetched
    if (this.stats) {
      return;
    }

    const cachedStats = statsCacheByUserId.get(this.userId);
    if (cachedStats) {
      this.stats = cachedStats;
      return;
    }

    let pendingFetch = statsFetchByUserId.get(this.userId);
    if (!pendingFetch) {
      pendingFetch = Auth.fetchWithIdToken(`/api/subscriptions/${this.userId}/stats`)
        .then((results) => {
          if (results.data) {
            statsCacheByUserId.set(this.userId, results.data);
          }
          return results.data ?? null;
        })
        .finally(() => {
          statsFetchByUserId.delete(this.userId);
        });
      statsFetchByUserId.set(this.userId, pendingFetch);
    }

    const stats = await pendingFetch;
    if (stats) {
      this.stats = stats;
    }
  }

  /**
   * Fetches a subscription object from a userId
   * @param {String} userId
   * @returns Subscription
   */
  static async fetchFromUserId(userId) {
    const snap = await collection.doc(userId).get();

    if (!snap.exists) {
      return null;
    }

    const sub = new Subscription(userId);
    sub.fetchFromDoc(snap);
    sub.fetchStats();

    return sub;
  }

  /**
   * @returns {Boolean} whether the subscription uses quota
   */
  get usesQuota() {
    if (this.currentPlan()) {
      const plan = this.currentPlan();
      return plan.version[0] === "2" || plan.version[0] === "3" || plan.version[0] === "4";
    } else {
      return true;
    }
  }

  /**
   * Determines if the subscription should have access to v4 features pertaining to credits
   * @returns {Boolean} whether the subscription uses credits
   */
  get usesLedgers() {
    return this.isV4 && (this.activeTrial() || (this.activeSubscription() && this.currentPlan().version[0] === "4"));
  }

  /**
   * Whether the subscription is v1 pricing
   */
  get isV1() {
    return this.data.version[0] === "1";
  }

  /**
   * Whether the subscription is v2 pricing
   */
  get isV2() {
    return this.data.version[0] === "2";
  }

  /**
   * Whether the subscription is v3 pricing
   */
  get isV3() {
    return this.data.version[0] === "3";
  }

  get isV4() {
    return this.data.version[0] === "4";
  }

  // Returns the id of the subscription (which is also the userId)
  get id() {
    return this.userId;
  }

  get throttled() {
    return !!this.data.throttled;
  }

  // The number of words left in the quota for this given billing cycle
  get quota() {
    return this.data.quota;
  }

  get paused() {
    return !!this.data.paused;
  }

  get hasTaxLocationAccessBlock() {
    return hasTaxLocationAccessBlock({
      taxLocationIssue: this.data.taxLocationIssue,
      taxLocationIssueDetectedAt: this.data.taxLocationIssueDetectedAt,
      paused: this.paused,
    });
  }

  // pack purchased quota (never expires aka always rolls over)
  get packQuota() {
    return this.data.packQuota || 0;
  }

  get hasPaid() {
    return !!this.data.hasPaid;
  }

  get isRevenueCatSubscription() {
    return this.activeSubscription() && ["app_store", "play_store"].includes(this.data?.subscriptionSource || "");
  }

  get requiresTurnstile() {
    if (this.activeSubscription()) {
      return false;
    }
    if (isQATurnstileBypassEmail(this.data.email)) {
      return false;
    }
    if (!this.data.castle) {
      return false;
    }
    if (this.data.turnstileVerified === true) {
      return false;
    }
    if (this.data.castle.filter?.policy?.action === "deny") {
      return true;
    }
    if (this.data.castle.risk?.policy?.action === "deny") {
      return true;
    }
    return false;
  }

  get showMultipleAccountCTA() {
    if (this.activeSubscription()) {
      return false;
    }
    if (!this.data.castle) {
      return false;
    }
    const metrics = this.data.castle.risk?.metrics || this.data.castle.filter?.metrics;
    if (!metrics) {
      return false;
    }
    if (metrics.users_per_device_fingerprint?.value > 2) {
      return true;
    }
    return false;
  }

  // this is the quota to be shown to users
  get totalQuota() {
    if (this.usesLedgers) {
      return parseInt(this.quota, 10);
    } else {
      return parseInt(this.quota, 10) + parseInt(this.packQuota, 10);
    }
  }

  get hasAccessToStoryEngineProseGenerate() {
    return !(this.activeTrial() && this.throttled);
  }

  get hasAccessToPluginTesting() {
    return this.hasAccessToManualPluginCreation;
  }

  get hasAccessToManualPluginCreation() {
    return this.features.adminProductPreview === true || !this.activeTrial();
  }

  get hasAccessToPluginCreation() {
    return this.features.adminProductPreview === true || !(this.activeTrial() && this.throttled);
  }

  get hasAccessToStoryEngine() {
    // because we forgot to gate legacy users at launch, we are still allowing them through the feature flag
    return (
      this.effectiveAdminForFeatures || //
      this.usesQuota ||
      this.activeTrial() ||
      !!this.features.storyEngineUngated
    ); // no subscription required;
  }

  get hasAccessToChat() {
    return (
      this.effectiveAdminForFeatures || //
      (!this.usesQuota && !!this.features.chatUngated) ||
      (this.usesQuota && !!this.features.chat)
    );
  }

  get hasAccessToStructuredOutline() {
    return this.effectiveAdminForFeatures || !!this.features.structuredOutline;
  }

  get hasAccessToReviser() {
    return this.effectiveAdminForFeatures || !!this.features.reviser;
  }

  get hasAccessToQuickEdit() {
    return true;
    // return this.admin || !!this.features.quickEdit;
  }

  get hasAccessToPlugins() {
    const plan = this.currentPlan();
    const isV4 = plan ? plan.usesCredits : this.isV4;
    return this.effectiveAdminForFeatures || isV4 || !!this.features.pluginsUngated || !!this.features.plugins;
  }

  get hasAccessToMyVoice() {
    return this.effectiveAdminForFeatures || !!this.features.myVoice;
  }

  get hasAccessToMyVoiceWrite() {
    return this.effectiveAdminForFeatures || !!this.features.myVoiceWrite;
  }

  get hasAccessToSpicy() {
    // return this.admin || !!this.features.spicy;
    return true;
  }

  get hasAccessToMuseBeta() {
    return this.effectiveAdminForFeatures || !!this.features.museBeta;
  }

  get hasAccessToMuse2AlphaPhase2() {
    return hasJoinedMuse2AlphaPhase2(this.features, this.data.muse2AlphaEnrollment);
  }

  get hasAccessToMuse2Alpha() {
    return this.effectiveAdminForFeatures || !!this.features.muse2Alpha;
  }

  get hasSeenProjectScenesModal() {
    return this.data.hasSeenProjectScenesModal;
  }

  get hasAccessToCleanCopy() {
    return true;
  }

  get isBannedFromSharingDocuments() {
    return !!this.data.isBannedFromSharingDocuments;
  }

  get hasAccessToChapterGlueBeta() {
    return true;
  }

  get hasAccessToPOVAndTenseBeta() {
    return true;
  }

  get hasAccessToMyVoiceV3A() {
    return this.effectiveAdminForFeatures || !!this.features.myVoiceV3A;
  }

  get hasAccessToMyVoiceV3B() {
    return this.effectiveAdminForFeatures || !!this.features.myVoiceV3B;
  }
  get hasAccessToStyleGuide() {
    return this.effectiveAdminForFeatures || !!this.features.styleGuide;
  }
  get hasAccessToAIFunctionEditor() {
    return hasAdminPermission("admin.tools");
  }
  get hasAccessToCharacterPortraits() {
    return true;
  }
  get hasAccessToWorldbuildingImages() {
    return true;
  }
  // Checklist creation is available to all writers.
  get hasAccessToChecklists() {
    return true;
  }

  get hasAccessToAgenticChat() {
    return true;
  }

  get hasAccessToAgenticNovelImport() {
    return !!this.features.agenticNovelImport;
  }

  get hasAccessToNewProjectFlow() {
    return true;
  }

  /** Explicit gate (no admin bypass): admins still need this for the chat brain model picker. */
  get hasAccessToAdminAgenticChatBrainPicker() {
    return !!this.features.adminAgenticChatBrainPicker;
  }

  get hasManuscriptFeedbackPlanAccess() {
    return this.hasAccessToFeedback;
  }

  get hasPaidTierManuscriptFeedbackAccess() {
    const currentPlan = this.currentPlan();

    if (this.activeTrial() && !currentPlan) {
      return true;
    }

    if (!this.activeSubscription() || !this.goodStanding()) {
      return false;
    }

    if (this.data.stripeStatus === "trialing") {
      return true;
    }

    return manuscriptFeedbackPaidPlanTitleAllowed(currentPlan?.title);
  }

  get hasAccessToFeedback() {
    return true;
  }

  get isFeedbackBetaUser() {
    return true;
  }

  /**
   * Check if user is in a percentage-based feature rollout.
   * Uses deterministic hashing so increasing percentage never excludes previous users.
   * @param {string} featureKey - Key from FEATURE_ROLLOUT
   * @returns {boolean}
   */
  isInFeatureRollout(featureKey) {
    const percentage = FEATURE_ROLLOUT[featureKey];
    if (percentage === undefined) return false;
    if (percentage >= 100) return true;
    if (percentage <= 0) return false;

    // Hash produces consistent value 0-99 for each user
    const userHash = Math.abs(hash(this.userId)) % 100;
    return userHash < percentage;
  }

  get hasAccessToTextHighlight() {
    return true;
  }

  get features() {
    return this.data.features || {};
  }

  /** Admin bypass for product feature gates; false when previewNonAdminFeatures is set. */
  get effectiveAdminForFeatures() {
    return computeEffectiveAdminForFeatures(this.features.adminProductPreview === true, this.features);
  }

  get discordId() {
    return this.data.discordId;
  }

  /**
   * The plan's interval ("month" or "year")
   */
  get interval() {
    return this.data.interval;
  }

  // Kept false for old renderers. Admin capabilities live outside subscriptions.
  get admin() {
    return false;
  }

  get showStoryBibleModal() {
    return !!this.data.showStoryBibleModal;
  }

  // Whether the user is currently in the grace quota period (they've hit 0 and we've refilled them a portion of quota)
  get grace() {
    if (!this.usesQuota) {
      return new Error("grace is only available in quota pricing plans");
    }

    return this.data.grace;
  }

  // Whether the user is allowed to go into overage in the current billing cycle
  get allowOverage() {
    if (!this.usesQuota) {
      return new Error("allowOverage is only available in quota pricing plan");
    }
    return this.data.allowOverage;
  }

  // The date when a paused subscription will resume being active
  // this is nearest billing cycle date after it resumes from stripe
  get resumesAtDisplay() {
    // const resumesDay = this.data.resumesAt.getDate();
    const currentPeriodEndAtResumesMonth = this.data.currentPeriodEnd;
    currentPeriodEndAtResumesMonth.setMonth(this.data.resumesAt.getMonth());

    // find the next billing cycle after resumesAt
    let date;
    if (this.data.resumesAt > currentPeriodEndAtResumesMonth) {
      // it will be the next month
      date = new Date(this.data.resumesAt);
      date.setDate(this.data.currentPeriodEnd.getDate());
      date.setMonth(this.data.resumesAt.getMonth() + 1);
    } else {
      date = new Date(this.data.resumesAt);
      date.setDate(this.data.currentPeriodEnd.getDate());
    }

    return date;
  }

  /**
   * Returns an array of plans in the same plan version universe
   * @param {Object} params
   * @param {String} params.interval
   * @returns
   */
  plans({ interval = null } = {}) {
    const plans = PRICING_PLANS[this.data.version].plans;

    if (interval) {
      return plans.filter((plan) => plan.interval === interval);
    } else {
      return plans;
    }
  }

  /**
   * Returns an array of plans to offer to the user
   * @param {Object} params
   * @param {String} params.interval
   * @returns
   */
  plansToOffer() {
    return PRICING_PLANS[this.data.version].plans.filter((plan) => !plan.hidden);
  }

  // Hydrate from a firebase subscription or a regular js object
  fetchFromDoc(doc) {
    let data = typeof doc.data == "function" ? doc.data() : doc.data;
    this.updateData(data);
    this.userId = doc.id;
    this.fetched = true;
  }

  // Whether the user is currently in an active trial
  activeTrial() {
    if (!this.fetched) {
      throw new Error("Must fetch first");
    }

    if (this.activeSubscription()) {
      return false;
    }

    if (this.usesQuota) {
      // for new pricing plan, you're on a trial if there is no plan
      return true;
    } else {
      // for legacy pricing plan, trial is a measure of time
      return new Date() < this.data.trialPeriodEnd;
    }
  }

  // Whether the trial is expired or not
  trialExpired() {
    if (!this.fetched) {
      throw new Error("Must fetch first");
    }

    if (this.usesQuota) {
      return false;
    } else {
      return new Date() > this.data.trialPeriodEnd;
    }
  }

  // Whether the subscription is canceled
  canceled() {
    if (!this.fetched) {
      throw new Error("Must fetch first");
    }

    return this.data.cancelAtPeriodEnd === true && !this.nextPlan();
  }

  // Whether the stripe subscription is in good standing (ie. not past overdue)
  goodStanding() {
    if (!this.activeSubscription()) {
      return false;
    } else {
      return this.data.stripeStatus === "active" || this.data.stripeStatus === "trialing";
    }
  }

  getFeatureFlags() {
    return {
      hasAccessToStoryEngine: this.hasAccessToStoryEngine,
      hasAccessToChat: this.hasAccessToChat,
      hasAccessToPlugins: this.hasAccessToPlugins,
      hasAccessToMyVoice: this.hasAccessToMyVoice,
      hasAccessToMyVoiceWrite: this.hasAccessToMyVoiceWrite,
      hasAccessToSpicy: this.hasAccessToSpicy,
      hasAccessToQuickEdit: this.hasAccessToQuickEdit,
      hasAccessToReviser: this.hasAccessToReviser,
      hasAccessToStructuredOutline: this.hasAccessToStructuredOutline,
      hasAccessToMuseBeta: this.hasAccessToMuseBeta,
      hasAccessToMuse2AlphaPhase2: this.hasAccessToMuse2AlphaPhase2,
      hasAccessToMuse2Alpha: this.hasAccessToMuse2Alpha,
      hasAccessToCleanCopy: this.hasAccessToCleanCopy,
      hasAccessToChapterGlueBeta: this.hasAccessToChapterGlueBeta,
      hasAccessToPOVAndTenseBeta: this.hasAccessToPOVAndTenseBeta,
      hasAccessToStoryEngineProseGenerate: this.hasAccessToStoryEngineProseGenerate,
      hasAccessToPluginTesting: this.hasAccessToPluginTesting,
      hasAccessToPluginCreation: this.hasAccessToPluginCreation,
      hasAccessToManualPluginCreation: this.hasAccessToManualPluginCreation,
      hasAccessToMyVoiceV3A: this.hasAccessToMyVoiceV3A,
      hasAccessToMyVoiceV3B: this.hasAccessToMyVoiceV3B,
      hasAccessToStyleGuide: this.hasAccessToStyleGuide,
      hasAccessToCharacterPortraits: this.hasAccessToCharacterPortraits,
      hasAccessToWorldbuildingImages: this.hasAccessToWorldbuildingImages,
      hasAccessToChecklists: this.hasAccessToChecklists,
      hasAccessToAgenticChat: this.hasAccessToAgenticChat,
      hasAccessToAgenticNovelImport: this.hasAccessToAgenticNovelImport,
      hasAccessToNewProjectFlow: this.hasAccessToNewProjectFlow,
      hasAccessToAdminAgenticChatBrainPicker: this.hasAccessToAdminAgenticChatBrainPicker,
      hasManuscriptFeedbackPlanAccess: this.hasManuscriptFeedbackPlanAccess,
      hasPaidTierManuscriptFeedbackAccess: this.hasPaidTierManuscriptFeedbackAccess,
      hasAccessToFeedback: this.hasAccessToFeedback,
    };
  }

  // Serialize for react state
  toJSON() {
    let functionProperties = {
      activeSubscription: this.activeSubscription(),
      goodStanding: this.goodStanding(),
      activeTrial: this.activeTrial(),
      trialExpired: this.trialExpired(),
      hasAccessToFeatures: this.hasAccessToFeatures(),
      planName: this.planName(),
      plans: this.plans(),
      canceled: this.canceled(),
      usesQuota: this.usesQuota,
      currentPlan: this.currentPlan(),
      currentPlanIsLegacy: this.currentPlanIsLegacy(),
      overageCost: this.overageCost(),
      overageWords: this.overageWords(),
      canPurchasePacks: this.canPurchasePacks(),
      lapsed: this.lapsed(),
      packs: this.packs,
      totalQuota: this.totalQuota,
      isV1: this.isV1,
      isV2: this.isV2,
      isV3: this.isV3,
      isV4: this.isV4,
      hasAccessToStoryEngine: this.hasAccessToStoryEngine,
      hasAccessToChat: this.hasAccessToChat,
      hasAccessToPlugins: this.hasAccessToPlugins,
      hasAccessToMyVoice: this.hasAccessToMyVoice,
      hasAccessToMyVoiceWrite: this.hasAccessToMyVoiceWrite,
      hasAccessToSpicy: this.hasAccessToSpicy,
      hasAccessToQuickEdit: this.hasAccessToQuickEdit,
      hasAccessToReviser: this.hasAccessToReviser,
      admin: this.admin,
      grace: this.grace,
      hasAccessToStoryEngineProseGenerate: this.hasAccessToStoryEngineProseGenerate,
      hasAccessToPluginTesting: this.hasAccessToPluginTesting,
      hasAccessToPluginCreation: this.hasAccessToPluginCreation,
      hasAccessToManualPluginCreation: this.hasAccessToManualPluginCreation,
      usesLedgers: this.usesLedgers,
      hasAccessToStructuredOutline: this.hasAccessToStructuredOutline,
      hasAccessToMuseBeta: this.hasAccessToMuseBeta,
      hasAccessToMuse2AlphaPhase2: this.hasAccessToMuse2AlphaPhase2,
      hasAccessToMuse2Alpha: this.hasAccessToMuse2Alpha,
      hasAccessToCleanCopy: this.hasAccessToCleanCopy,
      isBannedFromSharingDocuments: this.isBannedFromSharingDocuments,
      hasAccessToChapterGlueBeta: this.hasAccessToChapterGlueBeta,
      hasAccessToPOVAndTenseBeta: this.hasAccessToPOVAndTenseBeta,
      hasAccessToMyVoiceV3A: this.hasAccessToMyVoiceV3A,
      hasAccessToMyVoiceV3B: this.hasAccessToMyVoiceV3B,
      hasAccessToStyleGuide: this.hasAccessToStyleGuide,
      hasAccessToCharacterPortraits: this.hasAccessToCharacterPortraits,
      hasAccessToWorldbuildingImages: this.hasAccessToWorldbuildingImages,
      hasAccessToChecklists: this.hasAccessToChecklists,
      hasAccessToAgenticChat: this.hasAccessToAgenticChat,
      hasAccessToAgenticNovelImport: this.hasAccessToAgenticNovelImport,
      hasAccessToNewProjectFlow: this.hasAccessToNewProjectFlow,
      hasAccessToAdminAgenticChatBrainPicker: this.hasAccessToAdminAgenticChatBrainPicker,
      hasManuscriptFeedbackPlanAccess: this.hasManuscriptFeedbackPlanAccess,
      hasPaidTierManuscriptFeedbackAccess: this.hasPaidTierManuscriptFeedbackAccess,
      hasAccessToFeedback: this.hasAccessToFeedback,
      getFeatureFlags: this.getFeatureFlags(),
    };

    if (!this.usesQuota) {
      functionProperties.priceToOffer = this.priceToOffer();
    }

    return Object.assign({ data: this.data, id: this.id, stats: this.stats }, functionProperties);
  }

  auditLevel() {
    if (!this.data.auditLevel) {
      return 0;
    } else {
      return this.data.auditLevel;
    }
  }

  // Returns the current price to offer for the legacy plan
  priceToOffer() {
    if (this.usesQuota) {
      throw new Error("priceToOffer is invalid in the new pricing plan");
    }

    let price;

    if (this.data.stripePriceToOffer == process.env.REACT_APP_STRIPE_MEMBERSHIP_PLAN_25_DOLLARS) {
      price = "25";
    } else if (this.data.stripePriceToOffer == process.env.REACT_APP_STRIPE_MEMBERSHIP_PLAN_20_DOLLARS) {
      price = "20";
    } else if (this.data.stripePriceToOffer == process.env.REACT_APP_STRIPE_MEMBERSHIP_PLAN_15_DOLLARS) {
      price = "15";
    } else if (this.data.stripePriceToOffer == process.env.REACT_APP_STRIPE_MEMBERSHIP_PLAN_10_DOLLARS) {
      price = "10";
    } else if (this.data.stripePriceToOffer == process.env.REACT_APP_STRIPE_MEMBERSHIP_PLAN) {
      price = "5";
    } else {
      price = "10";
    }

    return price;
  }

  // Initiates a request to change the plan
  async changePlan({ stripePlanId, cancel, embedded, currentUrl }) {
    const result = await Auth.fetchWithIdToken(`/api/subscriptions/${this.userId}/changePlan`, {
      stripePlanId: stripePlanId,
      cancel: cancel,
      embedded: embedded,
      currentUrl: currentUrl,
      method: "PUT",
    });

    if (getConferenceCampaign(this.data?.conferenceCampaign?.id) && result.stripeCheckoutURL && !embedded) {
      // Hosted Checkout works directly from the server URL, including mobile browsers.
      window.location.assign(result.stripeCheckoutURL);
      return result;
    } else if (result.stripeSessionId && !embedded) {
      stripe.redirectToCheckout({
        sessionId: result.stripeSessionId,
      });
    } else {
      return result;
    }
  }

  static async anonymousSubscription({ stripePlanId }) {
    const result = await Auth.fetchWithIdToken(`/api/anonymous-subscription`, {
      stripePlanId: stripePlanId,
      method: "POST",
    });

    if (result.stripeSessionId) {
      stripe.redirectToCheckout({
        sessionId: result.stripeSessionId,
      });
    } else {
      return result;
    }
  }

  /**
   * Finds a plan from all plan universes
   * @param {String} id ID of plan to find
   * @param {Object} options
   * @param {String} options.scope Pricing version to find plan in. "all" to search through all versions. "version" to only look through the current version.
   * @returns {Object} false if not found and if found: { version: version number, plan: plan object detail }
   */
  findPlan(id, { scope = "version" } = {}) {
    let versions;

    if (scope === "version") {
      versions = [this.data.version];
    } else if (scope === "all") {
      versions = Object.keys(PRICING_PLANS).sort((a, b) => (a < b ? -1 : 1));
    } else {
      throw new Error("Invalid scope");
    }

    // traverse versions until we find the plan
    for (const version of versions) {
      const found = PRICING_PLANS[version].plans.find((plan) => plan.id === id);

      if (found) {
        return found;
      }
    }

    return null;
  }

  /* Returns the current plan, which is an object with fields:
   *    title
   *    description
   *    allotment (how much quota is refreshed to each month)
   *    price (in cents)
   *    allowOverage (whether overages are allowed)
   *    graceAmount (how much grace amount there is)
   */
  currentPlan() {
    if (this.data.stripePlanId) {
      const plan = this.findPlan(this.data.stripePlanId, { scope: "all" });

      if (plan) {
        return plan;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  /**
   * Returns true if the current plan is a legacy plan (ie. it is not offered in the current set of plans)
   */
  currentPlanIsLegacy() {
    if (!this.currentPlan()) {
      return false;
    }

    return !this.plansToOffer().find((plan) => plan.id === this.currentPlan().id);
  }

  // Whether they had a subscription before and has lapsed
  lapsed() {
    return !this.activeSubscription() && this.hasPaid && !this.paused;
  }

  /**
   * Returns Date object set to the next provisioning date (last date + one month)
   * @returns {Date}
   */
  nextProvisionDate() {
    if (!this.data.provisionedAt) {
      return null;
    }

    const provisionedAt = this.data.provisionedAt.toDate();
    return new Date(provisionedAt.setMonth(provisionedAt.getMonth() + 1));
  }

  // Returns the next plan if there is a scheduled plan change. If null, it means there is no
  // scheduled plan change. You can assume the change will happen at the end of the current billing cycle.
  nextPlan() {
    if (this.data.nextStripePlanId) {
      const plan = this.findPlan(this.data.nextStripePlanId, { scope: "all" });

      if (plan) {
        return plan;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  canPurchasePacks() {
    return (
      this.usesQuota &&
      !this.allowOverage &&
      this.currentPlan() &&
      this.currentPlan().allowPurchaseQuota &&
      this.goodStanding() &&
      !this.paused &&
      (!this.canceled() || (this.data.cancelAt && this.data.cancelAt > new Date()))
    );
  }

  /** The set of packs available for this sub */
  get packs() {
    if (this.canPurchasePacks()) {
      return this.usesLedgers ? PRICING_CREDIT_PACKS[this.currentPlan().packSet] : PRICING_PACKS[this.currentPlan().packSet];
    } else {
      return [];
    }
  }

  get V4Transition() {
    return this.data.V4Transition;
  }

  // attempts to claim grace amount for the plan. return true if successful.
  async claimGrace() {
    const result = await Auth.fetchWithIdToken(`/api/subscriptions/${this.userId}/claimGrace`, {});

    return result.success;
  }

  async resume() {
    const result = await Auth.fetchWithIdToken(`/api/subscriptions/${this.userId}/resume`, {});
    return result.success;
  }

  // Calculates the overages for the current cycle based on the plan
  overage() {
    return {
      cost: Math.round((OVERAGE_COST * (this.data.overage || 0)) / 1000),
      words: this.data.overage || 0,
    };
  }

  overageCost() {
    const overage = this.overage();
    return (overage.cost / 100).toFixed(2) || null;
  }

  overageWords() {
    const overage = this.overage();
    return overage.words || 0;
  }

  nextPlanDateOrNextBillingDate() {
    return this.data.nextStripePlanDate || this.nextBillingDate();
  }

  /*  Returns the date at which current plan ends */
  nextBillingDate() {
    if (this.data.currentPeriodEnd) {
      return this.data.currentPeriodEnd;
    } else {
      return null;
    }
  }

  // Note: DEPRECATED
  planName() {
    switch (this.data.stripeProductId) {
      case process.env.REACT_APP_STRIPE_MEMBERSHIP_PRODUCT:
        return "Monthly Membership";
      case process.env.REACT_APP_STRIPE_FREE_MEMBERSHIP_PRODUCT:
        return "Sudowrite Friend Membership";
      case process.env.REACT_APP_STRIPE_ENTERPRISE_PRODUCT:
        return "Sudowrite Enterprise Plan";
      default:
        return "Unknown Plan";
    }
  }

  // Whether the user is on an active subscription
  // Note that even though they may have an active subscription
  // their quota may be depleted
  activeSubscription() {
    if (!this.fetched) {
      throw new Error("Must fetch first");
    }

    const now = new Date();

    // if (this.data.canceledAt < now) {
    //   // subscription has been canceled
    //   return false;
    // } else {
    // check if within period
    return now < this.data.currentPeriodEnd;
    // }
  }

  // TODO: fix for admin
  isFreePlan() {
    return this.activeSubscription() && this.data.stripeProductId == process.env.REACT_APP_STRIPE_FREE_MEMBERSHIP_PRODUCT;
  }

  // Whether the user is allowed to access AI functions
  hasAccessToFeatures() {
    if (requiresConferencePayment(this.data)) return false;
    // Mirror the backend's post-recovery-window check so the UI does not offer
    // AI actions that the API will reject.
    if (this.hasTaxLocationAccessBlock) {
      return false;
    }

    if (this.usesQuota) {
      if (this.usesLedgers) {
        if (this.activeSubscription()) {
          if (!this.goodStanding()) {
            return false;
          } else {
            return this.totalQuota > 0;
          }
        } else if (this.lapsed()) {
          return false;
        } else {
          // trial mode
          return this.totalQuota > 0;
        }
      } else {
        if (this.activeSubscription()) {
          if (!this.goodStanding()) {
            return false;
          } else if (this.data.allowOverage && this.data.provisioned) {
            return true;
          } else {
            return this.totalQuota > 0;
          }
        } else {
          // trial mode
          return this.totalQuota > 0;
        }
      }
    } else {
      if (this.activeTrial()) {
        return true;
      } else if (this.activeSubscription()) {
        return this.goodStanding();
      } else {
        return false;
      }
    }
  }

  // Updates the model data from firebase data
  updateData(data) {
    this.data = data;

    if (this.data.trialPeriodEnd) this.data.trialPeriodEnd = Utils.dateFromTimestamp(this.data.trialPeriodEnd);
    if (this.data.currentPeriodEnd) this.data.currentPeriodEnd = Utils.dateFromTimestamp(this.data.currentPeriodEnd);
    if (this.data.nextStripePlanDate) this.data.nextStripePlanDate = Utils.dateFromTimestamp(this.data.nextStripePlanDate);
    if (this.data.memberSince) this.data.memberSince = Utils.dateFromTimestamp(this.data.memberSince);
    if (this.data.signedUpAt) this.data.signedUpAt = Utils.dateFromTimestamp(this.data.signedUpAt);
    if (this.data.cancelAt) this.data.cancelAt = Utils.dateFromTimestamp(this.data.cancelAt);
    if (this.data.canceledAt) this.data.canceledAt = Utils.dateFromTimestamp(this.data.canceledAt);
    if (this.data.resumesAt) this.data.resumesAt = Utils.dateFromTimestamp(this.data.resumesAt);
  }

  // Returns the current ledger associated with the Subscription based on provisionedAt date
  async fetchLedger() {
    const result = await Auth.fetchWithIdToken(`/api/subscriptions/${this.userId}/ledger`, {});

    return result;
  }

  // Returns recent ledger entries across all billing periods for the Recent Activity log
  async fetchRecentActivity() {
    const result = await Auth.fetchWithIdToken(`/api/subscriptions/${this.userId}/ledger/activity`, {});

    return result;
  }

  // returns the current ledger details associated with the Subscription based on provisionedAt date
  // i.e. { nonExpiringBalance, expiringBalance, balance }
  async fetchLedgerDetails() {
    const result = await Auth.fetchWithIdToken(`/api/subscriptions/${this.userId}/ledger-details`, {});

    return result;
  }
}
